/**
 * API Client for CityScrape
 */

import axios from 'axios';

// Helper function to normalize API URL (remove trailing slashes)
function normalizeApiUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

// Use backend URL directly - Railway deployment
// Remove trailing slashes to prevent double slashes in URLs
const API_URL = normalizeApiUrl(import.meta.env.VITE_API_URL || 'https://cityscrapeai-production.up.railway.app');

/** Expose for debugging: what API base URL the built app is using */
export const getApiBaseUrl = () => API_URL;

// Auth: simple password at /login only. No Bearer tokens; cookies sent via withCredentials if backend uses them.
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// On 401: only redirect to login if user hasn't passed the simple password gate.
// If they have (sessionStorage), stay on page so they see API error instead of being kicked back.
const BRIGHTSTONE_STORAGE_KEY = 'brightstone_unlocked';
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
      const passedSimpleGate = typeof window !== 'undefined' && sessionStorage.getItem(BRIGHTSTONE_STORAGE_KEY) === '1';
      if (!isLoginPage && !passedSimpleGate) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------
// Lightweight client-side cache with TTL + focus refresh
// ---------------------------------------------------------
type CacheEntry = {
  data: any;
  ts: number; // ms
  timer?: number;
  boundFocus?: boolean;
};

const cacheStore = new Map<string, CacheEntry>();

function buildKey(path: string, params?: any): string {
  const p = params ? `?${new URLSearchParams(params as any).toString()}` : '';
  return `${path}${p}`;
}

async function fetchWithCache(path: string, params: any, opts: { staleMs: number; cacheMs: number; refetchOnFocus?: boolean; pollMs?: number; }): Promise<any> {
  const key = buildKey(path, params);
  const now = Date.now();
  const entry = cacheStore.get(key);
  const isFresh = entry && now - entry.ts < opts.staleMs;

  // Serve fresh cache immediately
  if (isFresh) return entry!.data;

  // If stale or missing, fetch
  const doFetch = async () => {
    const res = await api.get(path, { params });
    cacheStore.set(key, { ...cacheStore.get(key), data: res.data, ts: Date.now(), timer: cacheStore.get(key)?.timer, boundFocus: cacheStore.get(key)?.boundFocus });
    return res.data;
  };

  // Bind focus refetch once per key
  let ce = entry;
  if (opts.refetchOnFocus && (!entry || !entry.boundFocus)) {
    const handler = () => {
      const e = cacheStore.get(key);
      if (!e) return;
      const stale = Date.now() - e.ts >= opts.staleMs;
      if (stale) doFetch().catch(() => {});
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handler);
    }
    ce = { ...(entry || { data: undefined, ts: 0 }), boundFocus: true } as CacheEntry;
  }

  // Start/keep polling if requested (only while tab focused by default)
  if (opts.pollMs && (!entry || !entry.timer)) {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') doFetch().catch(() => {});
    }, opts.pollMs);
    ce = { ...(ce || { data: undefined, ts: 0 }), timer: id } as CacheEntry;
  }

  // Save placeholder entry so focus/poll bindings persist
  if (ce && !entry) cacheStore.set(key, ce);

  const data = await doFetch();

  // Schedule GC for cacheMs
  window.setTimeout(() => {
    const current = cacheStore.get(key);
    if (!current) return;
    if (Date.now() - current.ts >= opts.cacheMs) {
      if (current.timer) window.clearInterval(current.timer);
      cacheStore.delete(key);
    }
  }, opts.cacheMs + 1000);

  return data;
}

// API methods
export const apiClient = {
  // Generic GET method
  get: async (url: string) => {
    const response = await api.get(url);
    return response.data;
  },

  // Generic POST method
  post: async (url: string, data?: any, config?: any) => {
    const response = await api.post(url, data, config);
    return response.data;
  },

  // Auth
  auth: {
    login: async (email: string, password: string) => {
      const response = await api.post('/api/custom-auth/login', { email, password });
      return response.data;
    },
    register: async (data: any) => {
      const response = await api.post('/api/custom-auth/register', data);
      return response.data;
    },
    me: async () => {
      const response = await api.get('/api/custom-auth/me');
      return response.data;
    },
    verifyMigratedUserPassword: async (email: string, password: string) => {
      const response = await api.post('/api/custom-auth/verify-migrated-user-password', { email, password });
      return response.data;
    },
    setClerkPassword: async (email: string, oldPassword: string, newPassword: string) => {
      const response = await api.post('/api/custom-auth/set-clerk-password', { 
        email, 
        old_password: oldPassword, 
        new_password: newPassword 
      });
      return response.data;
    },
    validatePassword: async (password: string, email?: string) => {
      const response = await api.post('/api/password/validate', { password, email });
      return response.data;
    },
    getTrialStatus: async () => {
      const response = await api.get('/api/custom-auth/trial-status');
      return response.data;
    },
    logout: async () => {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    },
    requestPasswordReset: async (email: string) => {
      const response = await api.post('/api/custom-auth/request-password-reset', { email });
      return response.data;
    },
    resetPassword: async (token: string, password: string) => {
      const response = await api.post('/api/custom-auth/reset-password-confirm', { token, password });
      return response.data;
    },
    acceptInvite: async (data: { token: string; password: string }) => {
      const response = await api.post('/api/custom-auth/accept-invite', data);
      return response.data;
    },
    createChatKitSession: async (data: { property_id: number }) => {
      // Create ChatKit session for property analysis
      const response = await api.post('/api/auth/chatkit/session', data);
      return response.data;
    },
  },

  // AI helpers
  ai: {
    enhanceDescription: async (payload: { category_name: string; user_description: string }) => {
      const response = await api.post('/api/ai/enhance-category-description', payload);
      return response.data;
    },
  },

  // Properties
  properties: {
    list: async () => {
      const response = await api.get('/api/properties/');
      return response.data;
    },
    get: async (id: number) => {
      const response = await api.get(`/api/properties/${id}`);
      return response.data;
    },
    create: async (data: any) => {
      const response = await api.post('/api/properties/', data);
      return response.data;
    },
    update: async (id: number, data: any) => {
      const response = await api.put(`/api/properties/${id}`, data);
      return response.data;
    },
    delete: async (id: number) => {
      const response = await api.delete(`/api/properties/${id}`);
      return response.data;
    },
    activate: async (id: number) => {
      const response = await api.post(`/api/properties/${id}/activate`);
      return response.data;
    },
    deactivate: async (id: number) => {
      const response = await api.post(`/api/properties/${id}/deactivate`);
      return response.data;
    },
    getRegions: async (sourceType?: string) => {
      const params = sourceType ? { source_type: sourceType } : {};
      const response = await api.get('/api/properties/regions', { params });
      return response.data;
    },
    getLandUseTypes: async () => {
      const response = await api.get('/api/properties/land-use-types');
      return response.data;
    },
    uploadDocument: async (propertyId: number, file: File, onProgress?: (progress: number) => void) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/properties/${propertyId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      });
      return response.data;
    },
    getDocuments: async (propertyId: number) => {
      const response = await api.get(`/api/properties/${propertyId}/documents`);
      return response.data;
    },
    generateSummary: async (propertyId: number) => {
      const response = await api.post(`/api/properties/${propertyId}/generate-summary`);
      return response.data;
    },
    mapData: async () => {
      const response = await api.get('/api/properties/');
      return response.data;
    },
  },

  // Development Applications
  developmentApplications: {
    list: async (municipality?: string, status?: string, buildingType?: string, limit?: number) => {
      const params: any = {};
      if (municipality) params.municipality = municipality;
      if (status) params.status = status;
      if (buildingType) params.building_type = buildingType;
      if (limit) params.limit = limit;
      const response = await api.get('/api/public/development-applications/', { params });
      return response.data;
    },
    map: async (municipality?: string, status?: string, buildingType?: string, limit?: number) => {
      const params: any = {};
      if (municipality) params.municipality = municipality;
      if (status) params.status = status;
      if (buildingType) params.building_type = buildingType;
      if (limit) params.limit = limit;
      const response = await api.get('/api/public/development-applications/map', { params });
      return response.data;
    },
    stats: async (municipality?: string) => {
      const params: any = {};
      if (municipality) params.municipality = municipality;
      const response = await api.get('/api/public/development-applications/stats', { params });
      return response.data;
    },
  },

  // Alerts removed - using pulses instead

  // Documents
  documents: {
    get: async (documentId: string) => {
      const response = await api.get(`/api/documents/${documentId}`);
      return response.data;
    },
    getContent: async (documentId: string) => {
      const response = await api.get(`/api/documents/${documentId}/content`);
      return response.data;
    },
    download: async (documentId: string) => {
      const response = await api.get(`/api/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },
    getAnalysis: async (documentId: string) => {
      const response = await api.get(`/api/documents/${documentId}/analysis`);
      return response.data;
    },
    reanalyze: async (documentId: string) => {
      const response = await api.post(`/api/documents/${documentId}/reanalyze`);
      return response.data;
    },
  },

  // Companies
  companies: {
    getInfo: async () => {
      const response = await api.get('/api/companies/me');
      return response.data;
    },
    updateSettings: async (settings: any) => {
      const response = await api.put('/api/companies/settings', settings);
      return response.data;
    },
    updateProfile: async (profile: { first_name?: string; last_name?: string }) => {
      const response = await api.put('/api/companies/profile', profile);
      return response.data;
    },
    updateProjectProfile: async (project_profile: any) => {
      const response = await api.put('/api/companies/project-profile', { project_profile });
      return response.data;
    },
    uploadToR: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/api/companies/terms-of-reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    getUsers: async () => {
      const response = await api.get('/api/companies/users');
      return response.data;
    },
    // User management methods removed - Brightstone internal use only
    updateMunicipalities: async (municipalities: string[]) => {
      const response = await api.put('/api/companies/municipalities', municipalities);
      return response.data;
    },
  },

  // Municipality Sources
  sources: {
    list: async () => {
      const response = await api.get('/api/municipalities/');
      return response.data;
    },
    update: async (id: number, enabled: boolean) => {
      const response = await api.put(`/api/municipalities/${id}`, { enabled });
      return response.data;
    },
    getProperties: async (municipalityId: number) => {
      const response = await api.get(`/api/municipalities/${municipalityId}/properties`);
      return response.data;
    },
  },

  // Company-level monitored municipalities
  companyMunicipalities: {
    list: async () => {
      const response = await api.get('/api/company-municipalities/');
      return response.data;
    },
    listAll: async () => {
      const response = await api.get('/api/company-municipalities/all');
      return response.data;
    },
    getStats: async () => {
      const response = await api.get('/api/company-municipalities/stats');
      return response.data;
    },
    add: async (municipalityId: number) => {
      const response = await api.post('/api/company-municipalities/', { municipality_id: municipalityId });
      return response.data;
    },
    remove: async (municipalityId: number) => {
      const response = await api.delete(`/api/company-municipalities/${municipalityId}`);
      return response.data;
    },
    switch: async (oldMunicipalityId: number, newMunicipalityId: number) => {
      const response = await api.post('/api/company-municipalities/switch', {
        old_municipality_id: oldMunicipalityId,
        new_municipality_id: newMunicipalityId
      });
      return response.data;
    },
    addCategoryMonitoring: async (municipalityId: number, categoryIds: number[]) => {
      const response = await api.post('/api/company-municipalities/add-category-monitoring', {
        municipality_id: municipalityId,
        category_ids: categoryIds
      });
      return response.data;
    },
  },

  // Committees
  committees: {
    getTypes: async () => {
      const response = await api.get('/api/committees/types');
      return response.data;
    },
    getByMunicipality: async (municipalityName: string) => {
      const response = await api.get(`/api/committees/municipality/${municipalityName}`);
      return response.data;
    },
    getByProperty: async (propertyId: number) => {
      const response = await api.get(`/api/committees/property/${propertyId}`);
      return response.data;
    },
    updatePropertyCommittees: async (propertyId: number, committeeIds: number[]) => {
      const response = await api.post(`/api/committees/property/${propertyId}/committees`, committeeIds);
      return response.data;
    },
  },

  // Meetings
  meetings: {
    list: async () => fetchWithCache('/api/meetings/', undefined, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true }),
    getMonitoredMunicipalities: async () => {
      const response = await api.get('/api/meetings/municipalities');
      return response.data;
    },
    getStats: async (days: number = 14) => {
      const response = await api.get(`/api/meetings/stats?days=${days}`);
      return response.data;
    },
    // Committee-based meetings
    getByCommittee: async (municipality?: string) => {
      const params = municipality ? { municipality } : undefined;
      return fetchWithCache('/api/meetings/by-committee', params, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true });
    },
    getCommitteeMeetings: async (committeeName: string, municipality?: string, limit: number = 50, offset: number = 0) => {
      const params: any = { limit, offset };
      if (municipality) params.municipality = municipality;
      const response = await api.get(`/api/meetings/committee/${encodeURIComponent(committeeName)}`, { params });
      return response.data;
    },
       getAttachments: async (meetingId: number) => {
         return fetchWithCache(`/api/meetings/${meetingId}/attachments`, undefined, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true });
       },
      getDocumentUrl: (storageKey: string) => {
        return `${API_URL}/api/documents/${storageKey}/content`;
      },
      committeeMeetings: async () => {
        return fetchWithCache('/api/meetings/by-committee', undefined, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true });
      },
      track: async (meetingId: string) => {
        const response = await api.post(`/api/meetings/${meetingId}/track`);
        return response.data;
      },
  },

  // Stripe - Company subscription ($250/month unlimited)
  stripe: {
    // Get company subscription status
    getCompanySubscription: async () => {
      const response = await api.get('/api/stripe/company-subscription');
      return response.data;
    },
    upgradeToOrgPlan: async () => {
      const response = await api.post('/api/stripe/upgrade-to-org-plan');
      return response.data;
    },
    // Create company checkout session
    createCompanyCheckout: async () => {
      const response = await api.post('/api/stripe/create-company-checkout');
      return response.data;
    },
    // Create checkout for municipality monitoring subscription
    createPropertyCheckout: async (propertyId: number) => {
      const response = await api.post('/api/stripe/property-checkout', {
        property_id: propertyId
      });
      return response.data;
    },
    subscribeToProperty: async (propertyId: number) => {
      const response = await api.post('/api/stripe/property-subscribe', {
        property_id: propertyId
      });
      return response.data;
    },
    createMunicipalityCheckout: async (municipalityId: number) => {
      const response = await api.post('/api/stripe/municipality-checkout', {
        municipality_id: municipalityId,
      });
      return response.data;
    },
    // Get customer's payment method
    getPaymentMethod: async () => {
      const response = await api.get('/api/stripe/payment-method');
      return response.data;
    },
    // Get municipality subscription price
    getMunicipalityPrice: async () => {
      const response = await api.get('/api/stripe/municipality-price');
      return response.data;
    },
    // Get municipality subscriptions with billing info
    getMunicipalitySubscriptions: async () => {
      const response = await api.get('/api/stripe/municipality-subscriptions');
      return response.data;
    },
    // Subscribe to municipalities using default payment method
    subscribeToMunicipalities: async (municipalityIds: number[]) => {
      const response = await api.post('/api/stripe/municipality-subscribe', {
        municipality_ids: municipalityIds,
      });
      return response.data;
    },
    // Attach payment method to customer
    attachPaymentMethod: async (setupIntentId: string) => {
      const response = await api.post('/api/stripe/attach-payment-method', {
        setup_intent_id: setupIntentId,
      });
      return response.data;
    },
    // Cancel company subscription
    cancelSubscription: async () => {
      const response = await api.post('/api/stripe/cancel-subscription');
      return response.data;
    },
    // Reactivate company subscription
    reactivateSubscription: async () => {
      const response = await api.post('/api/stripe/reactivate-subscription');
      return response.data;
    },
  },

  // Billing endpoints removed - Brightstone internal use only

  // Projects
  projects: {
    list: async () => {
      const response = await api.get('/api/projects/');
      return response.data;
    },
    get: async (projectId: number) => {
      const response = await api.get(`/api/projects/${projectId}`);
      return response.data;
    },
    create: async (project: any) => {
      const response = await api.post('/api/projects/', project);
      return response.data;
    },
    update: async (projectId: number, project: any) => {
      const response = await api.put(`/api/projects/${projectId}`, project);
      return response.data;
    },
    delete: async (projectId: number) => {
      const response = await api.delete(`/api/projects/${projectId}`);
      return response.data;
    },
  },

  // Project Insights
  projectInsights: {
    getCategories: async () => fetchWithCache('/api/project-insights/categories', undefined, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true }),
    getById: async (insightId: number) => {
      const response = await api.get(`/api/project-insights/by-id/${insightId}`);
      return response.data;
    },
    getByProperty: async (propertyId: number, category?: string, limit = 20, offset = 0) => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());
      
      const path = `/api/project-insights/${propertyId}`;
      const queryParams = Object.fromEntries(params.entries());
      return fetchWithCache(path, queryParams, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true });
    },
    // Get all insights for company (no property-specific, avoids duplicates)
    getAll: async (category?: string, limit?: number, offset = 0) => {
      try {
        const params: any = {};
        if (category) params.category = category;
        // No limit - fetch all insights
        if (limit && limit > 0) {
          params.limit = limit;
        }
        // Don't send limit if not specified - backend will return all
        params.offset = (offset && offset >= 0) ? offset : 0;
        // Note: Type filtering is handled at the pulses level, not insights level
        console.log('[API] getAll insights params:', params);
        const response = await api.get('/api/project-insights/items', { params });
        console.log('[API] getAll insights result:', response.data);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error: any) {
        console.error('[API] Error in getAll insights:', error?.response?.data || error?.message || error);
        return [];
      }
    },
    getNearbyApps: async (propertyId: number) => {
      const response = await api.get(`/api/project-insights/${propertyId}/nearby-apps`);
      return response.data;
    },
    getPrecedents: async (propertyId: number) => {
      const response = await api.get(`/api/project-insights/${propertyId}/precedents`);
      return response.data;
    },
    getSimilarForm: async (propertyId: number) => {
      const response = await api.get(`/api/project-insights/${propertyId}/similar-form`);
      return response.data;
    },
    getAgendaItems: async (propertyId: number) => {
      const response = await api.get(`/api/project-insights/${propertyId}/agenda`);
      return response.data;
    },
    getRegionalUpdates: async (propertyId: number) => {
      const response = await api.get(`/api/project-insights/${propertyId}/regional`);
      return response.data;
    },
    create: async (data: any) => {
      const response = await api.post('/api/project-insights/', data);
      return response.data;
    },
    update: async (insightId: number, data: any) => {
      const response = await api.patch(`/api/project-insights/${insightId}`, data);
      return response.data;
    },
    delete: async (insightId: number) => {
      const response = await api.delete(`/api/project-insights/${insightId}`);
      return response.data;
    },
    dismiss: async (insightId: number, propertyId: number, reason: 'finished' | 'irrelevant') => {
      const response = await api.post(`/api/project-insights/${insightId}/dismiss?property_id=${propertyId}`, { reason });
      return response.data;
    },
  },

  // Provincial
  provincial: {
    publicEvents: async (limit?: number, offset?: number) => {
      try {
        const params: any = {};
        if (limit !== undefined && limit !== null) params.limit = limit;
        if (offset !== undefined && offset !== null) params.offset = offset;
        console.log('[API] Fetching provincial public events with params:', params);
        const response = await api.get('/api/provincial/public-events', { params });
        console.log('[API] Provincial public events response:', response.data);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error: any) {
        console.error('[API] Error fetching provincial public events:', error?.response?.data || error?.message || error);
        return [];
      }
    },
    getPublicEventsCount: async () => {
      const response = await api.get('/api/provincial/public-events/count');
      return response.data;
    },
  },

    legal: {
      listDecisions: async (params?: any) => fetchWithCache('/api/legal-decisions', params, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true }),
      listAttachments: async (decisionId: string) => fetchWithCache(`/api/legal-decisions/${decisionId}/attachments`, undefined, { staleMs: 5 * 60_000, cacheMs: 15 * 60_000, refetchOnFocus: true }),
      getDecisionCount: async (tribunal?: string) => {
        const response = await api.get('/api/legal-decisions/count', { params: tribunal ? { tribunal } : undefined });
        return response.data;
      },
      listTribunals: async () => {
        const response = await api.get('/api/legal-tribunals');
        return response.data;
      },
      getActsStats: async () => {
        const response = await api.get('/api/legal-stats/acts');
        return response.data;
      },
      getSectionsStats: async (actName: string) => {
        const response = await api.get(`/api/legal-stats/sections/${encodeURIComponent(actName)}`);
        return response.data;
      },
    },

    // Reports API
    reports: {
      create: async (data: {
        subject: string;
        category: string;
        description: string;
        priority: string;
      }) => {
        const response = await api.post('/api/reports/', data);
        return response.data;
      },
      
      list: async () => {
        const response = await api.get('/api/reports/');
        return response.data;
      },
      
      get: async (reportId: number) => {
        const response = await api.get(`/api/reports/${reportId}`);
        return response.data;
      },
      
      listAll: async () => {
        const response = await api.get('/api/reports/admin/all');
        return response.data;
      },
    },

  // Account Security
  security: {
    getSessions: async () => {
      const response = await api.get('/api/auth/sessions');
      return response.data;
    },
    revokeSession: async (sessionToken: string) => {
      const response = await api.delete(`/api/auth/sessions/${sessionToken}`);
      return response.data;
    },
    revokeAllSessions: async () => {
      const response = await api.delete('/api/auth/sessions/revoke-all');
      return response.data;
    },
    getPasswordHistory: async () => {
      const response = await api.get('/api/auth/password-history');
      return response.data;
    },
    getSecuritySettings: async () => {
      const response = await api.get('/api/auth/security-settings');
      return response.data;
    },
  },

  // Two-Factor Authentication
  twoFactor: {
    getStatus: async () => {
      const response = await api.get('/api/two-factor/status');
      return response.data;
    },
    setup: async () => {
      const response = await api.post('/api/two-factor/setup');
      return response.data;
    },
    verify: async (code: string) => {
      const response = await api.post('/api/two-factor/verify', { code });
      return response.data;
    },
    enable: async (code: string) => {
      const response = await api.post('/api/two-factor/enable', { code });
      return response.data;
    },
    disable: async (password: string, code?: string) => {
      const response = await api.post('/api/two-factor/disable', { password, code });
      return response.data;
    },
    regenerateBackupCodes: async () => {
      const response = await api.post('/api/two-factor/regenerate-backup-codes');
      return response.data;
    },
    verifyLogin: async (email: string, code: string) => {
      const response = await api.post('/api/two-factor/verify-login', { email, code });
      return response.data;
    },
  },

  // Mayoral Decisions
  mayoralDecisions: {
    list: async (params?: { municipality?: string; limit?: number; offset?: number }) => {
      const response = await api.get('/api/mayoral-decisions/', { params });
      return response.data;
    },
    get: async (decisionId: string) => {
      const response = await api.get(`/api/mayoral-decisions/${decisionId}`);
      return response.data;
    },
    getMunicipalities: async () => {
      const response = await api.get('/api/mayoral-decisions/stats/municipalities');
      return response.data;
    },
  },

  // Notifications
  notifications: {
    list: async (params?: { limit?: number; offset?: number; type?: string; unread_only?: boolean }) => {
      const response = await api.get('/api/notifications/', { params });
      return response.data;
    },
    getStats: async () => {
      // Stats are now included in the list response, but keep this for backward compatibility
      const response = await api.get('/api/notifications/', { params: { limit: 1 } });
      return response.data.stats;
    },
    markAsRead: async (notificationId: number) => {
      const response = await api.patch(`/api/notifications/${notificationId}/read`);
      return response.data;
    },
    markAllAsRead: async () => {
      const response = await api.post('/api/notifications/mark-all-read');
      return response.data;
    },
    delete: async (notificationId: number) => {
      const response = await api.delete(`/api/notifications/${notificationId}`);
      return response.data;
    },
    deleteAll: async () => {
      const response = await api.post('/api/notifications/dismiss-all');
      return response.data;
    },
    // Preferences
    getPreferences: async () => {
      const response = await api.get('/api/notifications/preferences/meetings');
      return response.data;
    },
    getMeetingPreference: async (meetingId: number) => {
      const response = await api.get(`/api/notifications/preferences/meetings/${meetingId}`);
      return response.data;
    },
    setMeetingPreference: async (meetingId: number, enabled: boolean) => {
      if (enabled) {
        // Enable: Create row
        const response = await api.post('/api/notifications/preferences/meetings', {
          meeting_id: meetingId
        });
        return response.data;
      } else {
        // Disable: Delete row
        const response = await api.delete(`/api/notifications/preferences/meetings/${meetingId}`);
        return response.data;
      }
    },
    deleteMeetingPreference: async (meetingId: number) => {
      const response = await api.delete(`/api/notifications/preferences/meetings/${meetingId}`);
      return response.data;
    },
  },

  // Records Search (Elasticsearch) with RAG Chat
  records: {
    search: async (params: { query: string; year?: string; limit?: number; offset?: number }) => {
      const response = await api.get('/api/records/search', { params });
      return response.data;
    },
    chat: async (data: { question: string; conversation_history?: Array<{role: string; content: string}>; search_type?: 'olt' | 'bylaw'; conversation_id?: string | null }) => {
      const response = await api.post('/api/records/chat', data);
      return response.data;
    },
  },

  // Watch Areas
  watchAreas: {
    list: async () => {
      const response = await api.get('/api/watch-areas/');
      return response.data;
    },
    create: async (data: { name: string; geometry: any; municipality?: string }) => {
      const response = await api.post('/api/watch-areas/', data);
      return response.data;
    },
    delete: async (id: number) => {
      const response = await api.delete(`/api/watch-areas/${id}`);
      return response.data;
    },
  },


  // Topics (formerly Custom Categories)
  topics: {
    list: async () => {
      const response = await api.get('/api/topics/');
      const data = response.data;
      if (!Array.isArray(data)) {
        console.warn('[api] topics.list got non-array response (wrong API URL?). Using [].');
        return [];
      }
      return data;
    },
    setPriority: async (categoryId: number) => {
      const response = await api.post(`/api/topics/${categoryId}/priority`);
      return response.data;
    },
    unsetPriority: async (categoryId: number) => {
      const response = await api.delete(`/api/topics/${categoryId}/priority`);
      return response.data;
    },
    getAll: async () => {
      const response = await api.get('/api/topics/');
      return response.data;
    },
    getAllDefaults: async () => {
      const response = await api.get('/api/topics/defaults/all');
      const data = response.data;
      if (!Array.isArray(data)) {
        console.warn('[api] topics.getAllDefaults got non-array response (wrong API URL?). Using [].');
        return [];
      }
      return data;
    },
    create: async (data: { 
      display_name: string; 
      description: string; 
      icon?: string;
      monitor_all?: boolean;
      monitoring_sources?: Array<{
        source_type: string;
        source_id: string;
        source_name: string;
      }>;
    }) => {
      const response = await api.post('/api/topics/', data);
      return response.data;
    },
    update: async (id: number, data: { 
      display_name?: string; 
      description?: string; 
      icon?: string; 
      is_active?: boolean;
      is_followed?: boolean;
      monitoring_sources?: Array<{
        source_type: string;
        source_id: string;
        source_name: string;
      }>;
    }) => {
      const response = await api.put(`/api/topics/${id}`, data);
      return response.data;
    },
    delete: async (id: number) => {
      const response = await api.delete(`/api/topics/${id}`);
      return response.data;
    },
    toggleDefault: async (slug: string) => {
      const response = await api.post(`/api/topics/toggle-default/${slug}`);
      return response.data;
    },
    // Onboarding endpoints
    getOnboardingStatus: async () => {
      const response = await api.get('/api/topics/onboarding/status');
      return response.data;
    },
    getOnboardingDefaults: async () => {
      const response = await api.get('/api/topics/onboarding/defaults');
      return response.data;
    },
    completeOnboarding: async (selectedCategories: string[], businessProfile?: any) => {
      const payload = {
        selected_categories: selectedCategories,
        business_profile: businessProfile
      };
      console.log('[API] completeOnboarding payload:', JSON.stringify(payload, null, 2));
      try {
        const response = await api.post('/api/topics/onboarding/complete', payload);
        console.log('[API] completeOnboarding response:', response.data);
        return response.data;
      } catch (error: any) {
        console.error('[API] completeOnboarding error:', error);
        console.error('[API] Error response:', error.response?.data);
        throw error;
      }
    },
  },

  // Category order management
  categoryOrder: {
    updateBatch: async (orders: Array<{ category_id: number; display_order: number }>) => {
      const response = await api.post('/api/category-order/batch', { orders });
      return response.data;
    },
  },

  // Public endpoints (no auth required)
  public: {
    getMunicipalities: async (sourceType: string = 'municipal') => {
      const response = await api.get(`/api/public/municipalities?source_type=${sourceType}`);
      return response.data;
    },
    getCoverage: async () => {
      const response = await api.get('/api/public/coverage');
      return response.data;
    },
    create: async (data: { name: string; source_type: string; parent_id?: number | null; source_url: string }) => {
      const response = await api.post('/api/public/municipalities', data);
      return response.data;
    },
  },

  // OLT Records
  olt: {
    search: async (query: string, conversationId?: number) => {
      const params = new URLSearchParams({ q: query });
      if (conversationId) {
        params.append('conversation_id', conversationId.toString());
      }
      const response = await api.get(`/api/olt/search?${params.toString()}`);
      return response.data;
    },
    getRecord: async (id: string) => {
      const response = await api.get(`/api/olt/records/${id}`);
      return response.data;
    },
  },

  // Bylaws Records
  bylaws: {
    search: async (query: string, conversationId?: number) => {
      const params = new URLSearchParams({ q: query });
      if (conversationId) {
        params.append('conversation_id', conversationId.toString());
      }
      const response = await api.get(`/api/bylaws/search?${params.toString()}`);
      return response.data;
    },
  },

  // Records Conversations
  recordsConversations: {
    list: async (searchType?: string) => {
      const params = searchType ? `?search_type=${searchType}` : '';
      const response = await api.get(`/api/records/conversations${params}`);
      return response.data;
    },
    get: async (id: number) => {
      const response = await api.get(`/api/records/conversations/${id}`);
      return response.data;
    },
    create: async (data: { search_type: string; title: string; elasticsearch_conversation_id?: string }) => {
      const response = await api.post('/api/records/conversations', data);
      return response.data;
    },
    delete: async (id: number) => {
      const response = await api.delete(`/api/records/conversations/${id}`);
      return response.data;
    },
    updateTitle: async (id: number, title: string) => {
      const response = await api.put(`/api/records/conversations/${id}/title?title=${encodeURIComponent(title)}`);
      return response.data;
    },
    updateElasticsearchId: async (id: number, elasticsearchId: string) => {
      const response = await api.put(`/api/records/conversations/${id}/elasticsearch-id?elasticsearch_conversation_id=${encodeURIComponent(elasticsearchId)}`);
      return response.data;
    },
  },
  ltb: {
    search: async (query: string, limit: number = 50) => {
      const response = await api.get('/api/ltb/search', {
        params: { q: query, limit },
      });
      return response.data;
    },
  },

  // Pulses API (Pulse Center Dashboard)
  pulses: {
    getStats: async (type?: 'business' | 'projects' | 'acquisitions' | 'project') => {
      const params = new URLSearchParams();
      if (type && ['business', 'projects', 'acquisitions', 'project'].includes(type)) {
        params.append('type', type);
      }
      const url = params.toString() ? `/api/pulses/stats?${params.toString()}` : '/api/pulses/stats';
      const response = await api.get(url);
      return response.data;
    },
    getDashboard: async (type?: 'business' | 'projects' | 'acquisitions' | 'project', additionalParams?: { project_id?: number; include_needs_review?: boolean; period?: string | null }) => {
      const params = new URLSearchParams();
      if (type && ['business', 'projects', 'acquisitions', 'project'].includes(type)) {
        params.append('type', type);
      }
      if (additionalParams?.project_id) {
        params.append('project_id', String(additionalParams.project_id));
      }
      if (additionalParams?.include_needs_review !== undefined) {
        params.append('include_needs_review', String(additionalParams.include_needs_review));
      }
      if (additionalParams?.period != null && additionalParams.period !== '') {
        params.append('period', additionalParams.period);
      }
      const url = params.toString() ? `/api/pulses/dashboard?${params.toString()}` : '/api/pulses/dashboard';
      const response = await api.get(url);
      return response.data;
    },
    getList: async (
      sourceId: string,
      sourceType: string,
      category?: string,
      type?: 'business' | 'projects' | 'acquisitions' | 'project',
      period?: string | null
    ) => {
      const params = new URLSearchParams({
        source_id: sourceId,
        source_type: sourceType,
      });
      if (category) {
        params.append('category', category);
      }
      if (type && ['business', 'projects', 'acquisitions', 'project'].includes(type)) {
        params.append('type', type);
      }
      if (period && period !== 'all') {
        params.append('period', period);
      }
      const response = await api.get(`/api/pulses/list?${params.toString()}`);
      return response.data;
    },
    /** Pulses found (created) within a date range for the current dashboard type. */
    getByFoundDate: async (
      type: 'business' | 'projects' | 'acquisitions',
      options?: {
        period?: 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';
        date_from?: string;
        date_to?: string;
        project_id?: number;
        include_needs_review?: boolean;
        municipality_id?: string | number | null;
      }
    ) => {
      const params = new URLSearchParams();
      params.append('type', type === 'business' ? 'general' : type);
      params.append('period', options?.period ?? 'today');
      if (options?.date_from) params.append('date_from', options.date_from);
      if (options?.date_to) params.append('date_to', options.date_to);
      if (options?.project_id != null) params.append('project_id', String(options.project_id));
      if (options?.include_needs_review !== undefined) {
        params.append('include_needs_review', String(options.include_needs_review));
      }
      if (options?.municipality_id != null && options.municipality_id !== '') {
        params.append('municipality_id', String(options.municipality_id));
      }
      const response = await api.get(`/api/pulses/by-found-date?${params.toString()}`);
      return response.data;
    },
    getDetail: async (insightId: number, sourceType: string = 'municipal') => {
      const response = await api.get(`/api/pulses/${insightId}?source_type=${sourceType}`);
      return response.data;
    },
    getPinned: async (type?: 'business' | 'projects' | 'acquisitions' | 'project') => {
      const params = new URLSearchParams();
      if (type && ['business', 'projects', 'acquisitions', 'project'].includes(type)) {
        params.append('type', type);
      }
      const url = params.toString() ? `/api/pulses/pinned/list?${params.toString()}` : '/api/pulses/pinned/list';
      const response = await api.get(url);
      return response.data;
    },
    pin: async (insightId: number, sourceType: string = 'municipal') => {
      const response = await api.post(`/api/pulses/${insightId}/pin?source_type=${sourceType}`);
      return response.data;
    },
    unpin: async (insightId: number) => {
      const response = await api.delete(`/api/pulses/${insightId}/pin`);
      return response.data;
    },
    dismiss: async (insightId: number) => {
      const response = await api.post(`/api/pulses/${insightId}/dismiss`);
      return response.data;
    },
    /** Submit a correction: this pulse should not have been created. Explanation is used to train the model. */
    submitCorrection: async (insightId: number, explanation: string) => {
      const response = await api.post(`/api/pulses/${insightId}/correction`, { explanation });
      return response.data;
    },
    /** Hide this pulse from the dashboard after marking a correction. Does not delete data. */
    hide: async (insightId: number) => {
      const response = await api.post(`/api/pulses/${insightId}/hide`);
      return response.data;
    },
    /** Fetch PDF bytes for a document (for View in PDF). Returns Blob. */
    getDocumentPdfBlob: async (documentId: number): Promise<Blob> => {
      try {
        const response = await api.get(`/api/pulses/documents/${documentId}/pdf`, {
          responseType: 'blob',
        });
        return response.data;
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
          let msg = "Failed to load PDF";
          try {
            const text = await (e.response.data as Blob).text();
            const j = JSON.parse(text) as { detail?: string };
            msg = j.detail || text || msg;
          } catch (_) { /* ignore */ }
          throw new Error(msg);
        }
        throw e;
      }
    },
    /** Fetch PDF bytes for a single item (for View in PDF). Returns null on 404 (no item PDF) so caller can fall back to document PDF. */
    getItemPdfBlob: async (documentId: number, itemNumber: string): Promise<Blob | null> => {
      const encoded = encodeURIComponent(itemNumber);
      try {
        const response = await api.get(`/api/pulses/documents/${documentId}/items/${encoded}/pdf`, {
          responseType: 'blob',
        });
        return response.data;
      } catch (e: unknown) {
        if (axios.isAxiosError(e) && e.response?.status === 404) return null;
        if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
          let msg = "Failed to load item PDF";
          try {
            const text = await (e.response.data as Blob).text();
            const j = JSON.parse(text) as { detail?: string };
            msg = j.detail || text || msg;
          } catch (_) { /* ignore */ }
          throw new Error(msg);
        }
        throw e;
      }
    },
    /** Fetch page dimensions for a document (for highlight overlay scaling). */
    getDocumentPages: async (
      documentId: number
    ): Promise<{ page_number: number; page_width_pts: number; page_height_pts: number }[]> => {
      const response = await api.get(`/api/pulses/documents/${documentId}/pages`);
      return response.data;
    },
  },
};

export default apiClient;
